import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVacancies } from '../hooks/useVacancies';
import { OpdrachtStatus } from '../hooks/useOpdrachten';
import { useAuthStore } from '../store/authStore';
import { Card, Button, Input } from '../components';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import {
  Search,
  Filter,
  MapPin,
  Clock,
  Euro,
  Briefcase,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';

export const VacaturesPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation('employer');
  const [search, setSearch] = useState('');
  const [professionalTypeFilter, setProfessionalTypeFilter] = useState('');
  const [workPreferenceFilter, setWorkPreferenceFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [opdrachtStatus] = useState<OpdrachtStatus | undefined>(undefined);

  const isProfessional = user?.role === 'professional';
  const isEmployer = user?.role === 'opdrachtgever' || user?.role === 'employer_member';


  const WORK_PREFERENCE_OPTIONS = [
    { value: '', label: t('vacatures.allPreferences') },
    { value: 'face_to_face', label: t('vacatures.faceToFace') },
    { value: 'hybrid_50_50', label: t('vacatures.hybrid') },
    { value: 'remote_only', label: t('vacatures.remoteOnly') },
  ];

  // Employer: fetch own vacancies
  const { data: employerVacanciesRaw, isLoading: isLoadingEmployerVacancies } = useQuery({
    queryKey: ['employer-vacancies-list', search, professionalTypeFilter],
    queryFn: async () => {
      const res = await api.get('/employer/vacancies');
      return res.data.data || [];
    },
    enabled: isEmployer,
  });

  const employerVacancies = useMemo(() => {
    if (!employerVacanciesRaw) return [];
    let items = employerVacanciesRaw as any[];
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((v: any) =>
        (v.titel || '').toLowerCase().includes(q) ||
        (v.beschrijving || '').toLowerCase().includes(q)
      );
    }
    if (professionalTypeFilter) {
      items = items.filter((v: any) => v.professional_type === professionalTypeFilter);
    }
    return items;
  }, [employerVacanciesRaw, search, professionalTypeFilter]);

  const { data: vacancyData, isLoading: isLoadingVacancies } = useVacancies({
    page,
    limit: 10,
    status: 'published',
    search: search || undefined,
    professional_type: professionalTypeFilter || undefined,
    work_preference: workPreferenceFilter || undefined,
  });

  const vacancies = vacancyData?.data || [];
  const pagination = vacancyData?.pagination;
  const displayItems = isEmployer
    ? employerVacancies
    : vacancies;
  const isLoading = isEmployer
    ? isLoadingEmployerVacancies
    : isLoadingVacancies;

  const getProfessionalTypeLabel = (type: string) =>
    PROFESSIONAL_TYPE_LABELS[type as keyof typeof PROFESSIONAL_TYPE_LABELS] || type.replace(/_/g, ' ');

  const getWorkPreferenceLabel = (pref: string) => {
    const found = WORK_PREFERENCE_OPTIONS.find(p => p.value === pref);
    return found?.label || pref;
  };

  const listLabel = isEmployer ? t('vacatures.vacancies') : t('vacatures.assignments');
  const listLabelLower = isEmployer ? t('vacatures.vacanciesLower') : t('vacatures.assignmentsLower');


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder={t('vacatures.searchPlaceholder', { label: listLabelLower })}
              icon={<Search />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="sm:w-48">
            <select
              value={professionalTypeFilter}
              onChange={(e) => {
                setProfessionalTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('vacatures.allTypes')}</option>
              {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
                <optgroup key={catId} label={cat.label}>
                  {cat.types.map(type => (
                    <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="sm:w-48">
            <select
              value={workPreferenceFilter}
              onChange={(e) => {
                setWorkPreferenceFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {WORK_PREFERENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? t('vacatures.hideFilters') : t('vacatures.moreFilters')}
          </Button>
        </div>
      </Card>

      {/* Results */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('vacatures.loadingLabel', { label: listLabel })}</p>
        </div>
      ) : (displayItems && displayItems.length > 0) ? (
        <>
          {/* Results count */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {isProfessional
                ? t('vacatures.resultsFound', { count: displayItems.length, label: listLabelLower })
                : t('vacatures.resultsFound', { count: pagination?.total || 0, label: listLabelLower })}
            </p>
          </div>

          {/* Vacancies/Opdrachten list */}
          <div className="space-y-6">
            {displayItems.map((item: any) => {
              // For opdrachten, use vacancy_id or id; for vacancies, use id
              const itemId = item.vacancy_id || item.id;
              const vacancy = isProfessional && opdrachtStatus !== undefined ? item : item;

              return (
              <Card
                key={itemId}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/opdrachten/${vacancy.id}`)}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {vacancy.employer_logo && (
                            <img
                              src={vacancy.employer_logo}
                              alt={vacancy.employer_company_name || t('vacatures.company')}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-lg font-medium text-gray-900">
                                {vacancy.titel}
                              </h3>
                              {isEmployer && vacancy.status && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  vacancy.status === 'published' ? 'bg-green-100 text-green-800' :
                                  vacancy.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                                  vacancy.status === 'closed' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {{ published: 'Gepubliceerd', draft: 'Concept', closed: 'Gesloten', filled: 'Ingevuld' }[vacancy.status as string] || vacancy.status}
                                </span>
                              )}
                            </div>
                            {vacancy.employer_company_name && (
                              <p className="text-sm text-gray-600">
                                {vacancy.employer_company_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {vacancy.beschrijving}
                        </p>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 mr-1" />
                        {getProfessionalTypeLabel(vacancy.professional_type)}
                      </div>
                      {vacancy.active_regions && vacancy.active_regions.length > 0 && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {vacancy.active_regions.join(', ')}
                        </div>
                      )}
                      {vacancy.work_preference && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {getWorkPreferenceLabel(vacancy.work_preference)}
                        </div>
                      )}
                      {vacancy.hours_per_month_staffel && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {t('vacatures.hoursPerMonth', { hours: vacancy.hours_per_month_staffel })}
                        </div>
                      )}
                      {vacancy.hourly_rate_max && (
                        <div className="flex items-center">
                          <Euro className="w-4 h-4 mr-1" />
                          {t('vacatures.maxRate', { rate: vacancy.hourly_rate_max })}
                        </div>
                      )}
                    </div>

                    {/* Languages */}
                    {vacancy.required_languages && vacancy.required_languages.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {vacancy.required_languages.map((lang: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="text-xs text-gray-500">
                      {t('vacatures.postedOn')} {vacancy.published_at
                        ? new Date(vacancy.published_at).toLocaleDateString('nl-NL')
                        : new Date(vacancy.created_at).toLocaleDateString('nl-NL')}
                      {vacancy.view_count > 0 && (
                        <span className="ml-2">
                          • {t('vacatures.viewCount', { count: vacancy.view_count })}
                        </span>
                      )}
                      {vacancy.application_count > 0 && (
                        <span className="ml-2">
                          • {t('vacatures.applicationCount', { count: vacancy.application_count })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {isEmployer && (
                    <div className="mt-4 lg:mt-0 lg:ml-6 flex-shrink-0">
                      <Link
                        to={`/employer/dashboard/zoeken/vacatures/${vacancy.id}/bewerken`}
                        onClick={e => e.stopPropagation()}
                      >
                        <Button variant="secondary" size="sm" className="w-full lg:w-auto">
                          Bewerken
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="mt-8 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  {t('vacatures.previous')}
                </Button>

                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === pagination.pages}
                  onClick={() => setPage(page + 1)}
                >
                  {t('vacatures.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="mx-auto h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('vacatures.noResultsTitle', { label: listLabelLower })}
          </h3>
          <p className="text-gray-600">
            {search || professionalTypeFilter || workPreferenceFilter
              ? t('vacatures.tryOtherFilters')
              : t('vacatures.noneAvailable', { label: listLabelLower })}
          </p>
        </Card>
      )}
    </div>
  );
};


